










































































































































































import { ClientAccountsApi } from '@/api/client-accounts';
import { InteractionApi } from '@/api/interaction';
import { UpdateInteractionRequest } from '@/api/requests/interaction/update-interaction-request';
import bookAppointmentDialog from '@/components/appointments/book-appointment-dialog.vue';
import loading from '@/components/loading.vue';
import { Appointment } from '@/shared/appointments/appointment';
import { ClientAccount } from '@/shared/clients/client-account';
import { Interaction } from '@/shared/interaction';
import { AppointmentCancellationType } from '@/shared/types/appointment-cancellation-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { InteractionLetterType } from '@/shared/types/interaction-letter-type';
import { InteractionOutcomeType } from '@/shared/types/interaction-outcome-type';
import { InteractionType } from '@/shared/types/interaction-type';
import { User } from '@/shared/user';
import { formatDate } from '@/shared/utilities/date-utilities';
import { DateTime } from 'luxon';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const clientApi = new ClientAccountsApi();
const interactionApi = new InteractionApi();
const typesModule = namespace('types');
const usersModule = namespace('users');

const MERIDIAN_AM = 'AM';
const MERIDIAN_PM = 'PM';

@Component({
    components: {
        bookAppointmentDialog,
        loading,
    },
})
export default class UpdateInteractionDialog extends Vue {
    @typesModule.Getter('interactionTypes') private interactionTypes!: Array<InteractionType>;
    @typesModule.Getter('interactionOutcomeTypes') private interactionOutcomeTypes!: Array<InteractionOutcomeType>;
    @typesModule.Getter('interactionLetterTypes') private interactionLetterTypes!: Array<InteractionLetterType>;
    @typesModule.Getter('appointmentTypes') private appointmentTypes!: Array<AppointmentType>;
    @typesModule.Getter('appointmentCancellationTypes') private appointmentCancellationTypes!: Array<AppointmentCancellationType>;

    @usersModule.Getter('users') private users!: Array<User>;

    @Prop({ required: true }) private visible!: boolean;
    @Prop({ required: true }) private client!: ClientAccount;
    @Prop({ required: true }) private originalInteraction!: Interaction;

    private isVisible: boolean = this.visible;
    private isLoading: boolean = true;
    private isSaving: boolean = false;
    private interaction: Interaction = { ...this.originalInteraction };
    private selectedClientId: string = this.originalInteraction.clients!.length > 1 ? 'both' : this.client.id!;
    private scheduleCallback: boolean = false;
    private allowFutureContact: boolean | null = null;
    private callbackDate: string | Date | null = null;
    private callbackHour: number | null = null;
    private callbackMinute: number | null = null;
    private callbackMeridian: string = MERIDIAN_AM;
    private useTwentyFourHours: boolean = false;
    private clientAppointments: Array<Appointment> | null = null;
    private partnerAppointments: Array<Appointment> | null = null;
    private selectedAppointment: Appointment | null = null;
    private selectedAppointmentCancellationTypeId: string | null = null;
    private bookAppointmentDialogVisible: boolean = false;

    private get interactionType(): InteractionType | null {
        if (this.interaction.interactionTypeId == null) {
            return null;
        }

        const interactionType = this.interactionTypes.find((type) => type.id === this.interaction.interactionTypeId);
        return interactionType == null ? null : interactionType!;
    }

    private get interactionOutcomeType(): InteractionOutcomeType | null {
        if (this.interaction.interactionOutcomeTypeId == null) {
            return null;
        }

        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.interaction.interactionOutcomeTypeId);
        return interactionOutcomeType == null ? null : interactionOutcomeType!;
    }

    private get filteredInteractionTypes(): Array<InteractionType> {
        if (this.interactionTypes == null || !this.interactionTypes.length) {
            return [];
        }

        return this.interactionTypes.filter((type) => !type.isArchived || this.interaction.interactionTypeId === type.id);
    }

    private get filteredInteractionOutcomeTypes(): Array<InteractionOutcomeType> {
        if (this.interactionOutcomeTypes == null || !this.interactionOutcomeTypes.length) {
            return [];
        }

        const activeInteractionOutcomeTypes = this.interactionOutcomeTypes.filter((type) => !type.isArchived || this.interaction.interactionOutcomeTypeId === type.id);

        if (this.interactionType == null) {
            return [];
        }

        const originalInteractionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.originalInteraction.interactionOutcomeTypeId);

        if (originalInteractionOutcomeType != null && originalInteractionOutcomeType.isAllowContact) {
            return activeInteractionOutcomeTypes.filter((type) => type.id === this.originalInteraction.interactionOutcomeTypeId);
        }

        if (this.interactionType.isCallMade) {
            return activeInteractionOutcomeTypes.filter((type) => type.isCallMadeOutcome && type.isAllowContact === this.client.doNotContact);
        }

        if (this.interactionType.isCallReceived) {
            return activeInteractionOutcomeTypes.filter((type) => type.isCallReceivedOutcome && type.isAllowContact === this.client.doNotContact);
        }

        if (this.interactionType.isLFTSY) {
            return activeInteractionOutcomeTypes.filter((type) => type.isLFTSYOutcome);
        }

        return [];
    }

    private get filteredInteractionLetterTypes(): Array<InteractionLetterType> {
        if (this.interactionLetterTypes == null || !this.interactionLetterTypes.length) {
            return [];
        }

        return this.interactionLetterTypes.filter((type) => !type.isArchived || this.interaction.letterTypeId === type.id);
    }

    private get filteredAppointmentCancellationTypes(): Array<AppointmentCancellationType> {
        if (this.appointmentCancellationTypes == null || !this.appointmentCancellationTypes.length) {
            return [];
        }

        return this.interaction.originalAppointment != null && this.interaction.originalAppointment.cancellationTypeId != null
            ? this.appointmentCancellationTypes.filter((type) => !type.isArchived || this.interaction.originalAppointment!.cancellationTypeId! === type.id)
            : this.appointmentCancellationTypes.filter((type) => !type.isArchived);
    }

    private get filteredClientAppointments(): Array<Appointment> {
        let appointments: Array<Appointment> | null;

        if (this.selectedClientId === this.client.id!) {
            if (this.partnerAppointments == null) {
                appointments = this.clientAppointments;
            } else {
                const partnerAppointmentIds = this.partnerAppointments.map((appointment) => appointment.id!);
                appointments = this.clientAppointments!.filter((appointment) => !partnerAppointmentIds.includes(appointment.id!));
            }
        } else if (this.client.partner != null && this.selectedClientId === this.client.partner.id!) {
            if (this.clientAppointments == null) {
                appointments = this.partnerAppointments;
            } else {
                const clientAppointmentIds = this.clientAppointments.map((appointment) => appointment.id!);
                appointments = this.partnerAppointments!.filter((appointment) => !clientAppointmentIds.includes(appointment.id!));
            }
        } else {
            if (this.partnerAppointments == null) {
                appointments = [];
            } else {
                const partnerAppointmentIds = this.partnerAppointments!.map((appointment) => appointment.id!);
                appointments = this.clientAppointments!.filter((appointment) => partnerAppointmentIds.includes(appointment.id!));
            }
        }

        return appointments == null
            ? []
            : appointments
                .filter((appointment) => (!appointment.isRebooked && appointment.outcome == null)
                    || (this.originalInteraction.originalAppointment != null && this.originalInteraction.originalAppointment.id! === appointment.id))
                .sort((a1, a2) => {
                    if (a1.date! > a2.date!) { return 1; }
                    if (a1.date! < a2.date!) { return -1; }
                    return 0;
                });
    }

    private get hours(): Array<{ value: number, label: string }> {
        const result: Array<{ value: number, label: string }> = [];

        for (let i = 0; i < (this.useTwentyFourHours ? 24 : 12); i++) {
            let value: number = i;
            let label: string = value.toString();

            if (!this.useTwentyFourHours) {
                value = i + 1;
                label = value.toString();

                if (this.callbackMeridian === MERIDIAN_AM) {
                    if (value === 12) {
                        value = 0;
                    }
                } else {
                    if (value !== 12) {
                        value += 12;
                    }
                }
            }

            result.push({ value, label: label.padStart(2, '0') });
        }

        return result;
    }

    private get minutes(): Array<{ value: number, label: string }> {
        return [
            { value: 0, label: '00' },
            { value: 15, label: '15' },
            { value: 30, label: '30' },
            { value: 45, label: '45' },
        ];
    }

    private get isFutureContactConfirmationRequired(): boolean {
        if (this.interaction.interactionOutcomeTypeId == null) {
            return false;
        }

        return this.interactionOutcomeType == null ? false : this.interactionOutcomeType.isWillVoid || this.interactionOutcomeType.isWillReleased;
    }

    private get isAppointmentRequired(): boolean {
        return this.interactionType == null || this.interactionOutcomeType == null ? false : this.interactionType.isLFTSY || this.interactionOutcomeType.isAppointmentRebooked || this.interactionOutcomeType.isAppointmentCancelled;
    }

    private get isAppointmentCancellationTypeRequired(): boolean {
        return this.interactionOutcomeType == null ? false : this.interactionOutcomeType.isAppointmentCancelled;
    }

    private get isCallbackRequired(): boolean {
        return this.interactionOutcomeType == null ? false : this.interactionOutcomeType.isCallback || this.scheduleCallback;
    }

    private get isLetterTypeRequired(): boolean {
        return this.interactionType == null ? false : this.interactionType.isLetterTypeRequired;
    }

    private get formatClientAndPartnerNames(): string {
        const clientFirstName = this.client.firstName != null ? this.client.firstName.trim() : '';
        const clientLastName = this.client.lastName != null ? this.client.lastName.trim() : '';

        if (this.client.partner == null) {
            return `${clientFirstName} ${clientLastName}`;
        }

        const partnerFirstName = this.client.partner.firstName != null ? this.client.partner.firstName.trim() : '';
        const partnerLastName = this.client.partner.lastName != null ? this.client.partner.lastName.trim() : '';

        return clientLastName.toLowerCase() === partnerLastName.toLowerCase()
            ? `${clientFirstName} and ${partnerFirstName} ${clientLastName}`.trim()
            : `${clientFirstName} ${clientLastName} and ${partnerFirstName} ${partnerLastName}`.trim();
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.$validator.errors.clear();
        this.isVisible = visible;
    }

    @Watch('interaction.interactionTypeId')
    private onInteractionTypeChanged(): void {
        this.interaction.interactionOutcomeTypeId = null;
        this.interaction.letterTypeId = null;
        this.selectedAppointment = null;
        this.selectedAppointmentCancellationTypeId = null;
        this.scheduleCallback = false;
    }

    @Watch('interaction.interactionOutcomeTypeId')
    private onInteractionOutcomeTypeChanged(): void {
        this.selectedAppointment = null;
        this.selectedAppointmentCancellationTypeId = null;
        this.scheduleCallback = false;
    }

    @Watch('useTwentyFourHours')
    private onUseTwentyFourHoursChanged(): void {
        if (this.callbackHour == null) {
            return;
        }

        this.callbackMeridian = this.callbackHour >= 12 ? MERIDIAN_PM : MERIDIAN_AM;
    }

    private appointmentTypeDescription(appointmentTypeId: string | null): string {
        if (appointmentTypeId == null) {
            return '';
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === appointmentTypeId);
        return appointmentType == null ? '' : appointmentType.value;
    }

    private legalAdvisorName(userId: string | null): string {
        if (userId == null) {
            return '';
        }

        const user = this.users.find((u) => u.id === userId);
        return user == null ? '' : `${user.firstName.trim()} ${user.lastName.trim()}`.trim();
    }

    private async onSave(): Promise<void> {
        const isValid = await this.$validator.validateAll();

        if (!isValid) {
            return;
        }

        this.isSaving = true;

        try {
            const clientIds: Array<string> = new Array<string>();

            if (this.selectedClientId === 'both') {
                clientIds.push(this.client.id!);
                clientIds.push(this.client.partner!.id!);
            } else {
                clientIds.push(this.selectedClientId);
            }

            const request: UpdateInteractionRequest = {
                clientIds,
                ...this.interaction,
                interactionTypeId: this.interaction.interactionTypeId!,
                notes: this.interaction.notes!,
                callbackDate: this.isCallbackRequired ? this.interaction.callbackDate = DateTime.fromJSDate(new Date(this.callbackDate!))
                    .set({ hour: this.callbackHour!, minute: this.callbackMinute! })
                    .toUTC()
                    .toJSDate() : null,
                allowFutureContact: this.isFutureContactConfirmationRequired ? this.allowFutureContact! : null,
                originalAppointmentId: this.selectedAppointment == null ? null : this.selectedAppointment.id!,
                appointmentCancellationTypeId: this.isAppointmentCancellationTypeRequired ? this.selectedAppointmentCancellationTypeId! : null,
                version: this.interaction.version!,
            };

            await interactionApi.updateInteractionAsync(this.interaction.id!, request);

            if (this.interactionType != null && this.interactionType.isLFTSY && this.originalInteraction.originalAppointment != null && this.originalInteraction.originalAppointment.id === this.selectedAppointment!.id!) {
                this.$emit('ok', { ...this.interaction });
            } else if (this.interactionOutcomeType != null && this.interactionOutcomeType.isAppointmentBooked && this.originalInteraction.interactionOutcomeTypeId !== this.interaction.interactionOutcomeTypeId) {
                this.selectedAppointment = null;
                this.bookAppointmentDialogVisible = true;
            } else if (this.interactionOutcomeType != null && this.interactionOutcomeType.isAppointmentRebooked && this.selectedAppointment != null) {
                this.bookAppointmentDialogVisible = true;
            } else {
                this.$emit('ok', { ...this.interaction });
            }
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not update interaction, please try again.', type: 'is-danger' });
            }
        }

        this.isSaving = false;
    }

    private onCancel(): void {
        this.bookAppointmentDialogVisible = false;
        this.$emit('cancel');
    }

    private async onAppointmentBooked(): Promise<void> {
        this.bookAppointmentDialogVisible = false;
        this.$snackbar.open({ message: 'Appointment succesfully booked.', type: 'is-success' });
        this.$emit('ok', { ...this.interaction });
    }

    private async onAppointmentCancel(): Promise<void> {
        this.bookAppointmentDialogVisible = false;
        this.$emit('ok', { ...this.interaction });
    }

    private onCallbackMeridianChanged(value: string | null): void {
        if (this.callbackHour == null) {
            return;
        }

        if (value === MERIDIAN_AM) {
            this.callbackHour = this.callbackHour === 12 ? 0 : this.callbackHour - 12;
        } else {
            this.callbackHour = this.callbackHour === 0 ? 12 : this.callbackHour + 12;
        }
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`users/loadAllUsersAsync`),
        ]);

        if (this.interaction.allowFutureContact != null) {
            this.allowFutureContact = this.interaction.allowFutureContact!;
        }

        if (this.interaction.callbackDate != null) {
            const callbackDate = formatDate(this.interaction.callbackDate);

            this.callbackDate = callbackDate;
            this.callbackMeridian = callbackDate!.getHours() >= 12 ? MERIDIAN_PM : MERIDIAN_AM;
            this.callbackHour = callbackDate!.getHours();
            this.callbackMinute = callbackDate!.getMinutes();
        }

        this.clientAppointments = await clientApi.getClientAppointmentBookingsAsync(this.client.id!);
        this.partnerAppointments = this.client.partner != null ? await clientApi.getClientAppointmentBookingsAsync(this.client.partner.id!) : [];

        if (this.interaction.originalAppointment != null && this.interaction.originalAppointment.id != null) {
            let originalAppointment = this.clientAppointments.find((appointment) => appointment.id! === this.interaction.originalAppointment!.id!);

            if (originalAppointment == null) {
                originalAppointment = this.partnerAppointments.find((appointment) => appointment.id! === this.interaction.originalAppointment!.id!);
            }

            this.selectedAppointment = originalAppointment == null ? null : originalAppointment;

            if (this.isAppointmentCancellationTypeRequired) {
                this.selectedAppointmentCancellationTypeId = this.interaction.originalAppointment!.cancellationTypeId!;
            }

            if (this.interaction.callbackDate != null) {
                this.scheduleCallback = true;
            }
        }

        this.isLoading = false;
    }
}
